import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./faq.css";
import { useLanguage } from "../../i18n/LanguageContext";

const Faq = () => {
  const { tr } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth <= 768;
    return false;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const faqItems = [
    {
      question: tr("What is Metagent?", "Apa itu Metagent?"),
      answer: tr(
        "Metagent is a software house that builds custom AI tools to help automate your daily work. Whether you're a solopreneur, creator, or team, we turn your ideas into real AI assistants",
        "Metagent adalah software house yang membangun tools AI kustom untuk mengotomasi pekerjaan harianmu. Baik solopreneur, creator, atau tim, kami mengubah ide menjadi AI assistant yang nyata."
      ),
    },
    {
      question: tr("Is this a product or a service?", "Ini produk atau layanan?"),
      answer: tr(
        "This is a service. We don't sell ready-made tools - instead, we build AI solutions tailored specifically to how you work",
        "Ini layanan. Kami tidak menjual tools siap pakai — kami membangun solusi AI yang disesuaikan dengan cara kerjamu."
      ),
    },
    {
      question: tr("Who is this for?", "Ini untuk siapa?"),
      answer: tr(
        "Metagent is great for solopreneurs, content creators, online business owners, and professionals who want to automate tasks, save time, and simplify their workflows with AI",
        "Metagent cocok untuk solopreneur, content creator, online business owner, dan profesional yang ingin mengotomasi tugas, hemat waktu, dan menyederhanakan workflow dengan AI."
      ),
    },
    {
      question: tr("I don't know what I need yet - can I still reach out?", "Saya belum tahu kebutuhan saya — bisa tetap kontak?"),
      answer: tr(
        "Yes, definitely. Just fill out the form and tell us what your work looks like. We'll help map your needs to the right AI solution",
        "Bisa. Cukup isi form dan ceritakan pekerjaanmu. Kami bantu memetakan kebutuhanmu ke solusi AI yang tepat."
      ),
    },
    {
      question: tr("What services or packages are available?", "Paket atau layanan apa yang tersedia?"),
      answerList: [
        tr(
          "Prototype ($999) - Ideal for testing a single AI workflow or automation",
          "Prototype ($999) - Ideal untuk menguji satu workflow atau automasi AI"
        ),
        tr(
          "Custom AI Solution - For larger or more complex needs, with flexible pricing based on scope",
          "Custom AI Solution - Untuk kebutuhan lebih besar/kompleks, harga fleksibel sesuai scope"
        ),
      ],
    },
    {
      question: tr("How do I know which one to choose?", "Bagaimana memilih paket yang tepat?"),
      answer: tr(
        "If you're unsure, just use the form to share what you're looking to automate or improve. We'll guide you to the right package",
        "Jika belum yakin, isi form tentang apa yang ingin kamu otomasi atau perbaiki. Kami akan mengarahkan ke paket yang tepat."
      ),
    },
    {
      question: tr("Do I need to be technical to work with you?", "Apakah harus teknis untuk bekerja dengan kalian?"),
      answer: tr(
        "Not at all. You explain what you want in simple terms - we translate that into working AI tools",
        "Tidak perlu. Kamu jelaskan dengan bahasa sederhana — kami terjemahkan menjadi tools AI yang berjalan."
      ),
    },
    {
      question: tr("How long does it take to build?", "Berapa lama proses build?"),
      answerList: [
        tr("Prototype: usually 2 to 4 weeks", "Prototype: biasanya 2–4 minggu"),
        tr("Custom: timeline depends on the scope and complexity", "Custom: tergantung scope dan kompleksitas"),
      ],
    },
    {
      question: tr("What tools or platforms can you integrate with?", "Tools atau platform apa saja yang bisa diintegrasikan?"),
      answer: tr(
        "We work with tools like Notion, WhatsApp, Google Sheets, Airtable, Slack, and many others. Mention what you use in the form",
        "Kami bisa bekerja dengan tools seperti Notion, WhatsApp, Google Sheets, Airtable, Slack, dan lainnya. Sebutkan yang kamu pakai di form."
      ),
    },
    {
      question: tr("Can I upgrade later if I start small?", "Apakah bisa upgrade nanti jika mulai kecil?"),
      answer: tr(
        "Yes. You can always start with a prototype and upgrade to a custom project later",
        "Bisa. Kamu bisa mulai dari prototype lalu upgrade ke custom project."
      ),
    },
    {
      question: tr("Is there support included?", "Apakah ada support?"),
      answer: tr(
        "Yes. All packages come with support during the build, and long-term support is available for custom solutions",
        "Ada. Semua paket termasuk support selama build, dan support jangka panjang tersedia untuk solusi custom."
      ),
    },
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="faq-page">
      <section className="faq-section">
        <Breadcrumbs />
        <motion.h1
          className="faq-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.5 }}
        >
          {tr("FAQ: Frequently Asked Questions", "FAQ: Pertanyaan yang Sering Ditanyakan")}
        </motion.h1>

        <div className="faq-list">
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              className="faq-item"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: index * 0.04,
                duration: 0.35,
              }}
            >
              <button
                type="button"
                className="faq-question-row"
                onClick={() => toggle(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
                id={`faq-question-${index}`}
              >
                <span className="faq-question-text">{item.question}</span>
                <span className="faq-icon" aria-hidden="true">
                  {openIndex === index ? (
                    <Minus size={22} strokeWidth={2.5} />
                  ) : (
                    <Plus size={22} strokeWidth={2.5} />
                  )}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    className="faq-answer-wrap"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="faq-answer">
                      {item.answerList ? (
                        <ul className="faq-answer-list">
                          {item.answerList.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>{item.answer}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Faq;
